import { useState } from 'react';
import { RiCloseCircleLine } from 'react-icons/ri';

import { subscriptionStatus } from '../utils/subscriptionStatus';
import showUserRole from './showUserRole';

import useCancelSubscription from '@/hooks/users/useCancelSubscription';
import useUpdateUserSubscription from '@/hooks/users/useUpdateUserSubscription';
import { AppRole } from '@/interfaces/entities/app.role';
import { SubscriberPlanEnum } from '@/interfaces/entities/subscription.';
import { IUser } from '@/interfaces/entities/user';

interface ManageSubscriptionBlockProps {
	user: IUser;
	closeModal: () => void;
}

const ManageSubscriptionBlock = ({
	user,
	closeModal,
}: ManageSubscriptionBlockProps) => {
	const updateUserSubscription = useUpdateUserSubscription();
	const cancelSubscription = useCancelSubscription();
	const [isLoading, setIsLoading] = useState(false);

	const handleSubscription = async (
		e: React.MouseEvent<HTMLButtonElement>,
		userId: number,
		status: SubscriberPlanEnum,
	) => {
		setIsLoading(true);
		e.stopPropagation();
		await updateUserSubscription.mutateAsync({
			userId,
			status,
		});
		setIsLoading(false);
	};

	const handleCancelSubscription = async (
		e: React.MouseEvent<HTMLButtonElement>,
		userId: number,
	) => {
		setIsLoading(true);
		e.stopPropagation();
		await cancelSubscription.mutateAsync({ userId });
		setIsLoading(false);
	};

	const isCancellableSubscription =
		user.subscription &&
		!user.subscription.isPlanCancelled &&
		user.subscription.isHardcoded;

	return (
		<div className="bg-white rounded-lg shadow-lg py-1 px-1">
			<div className="flex justify-end">
				<button onClick={() => closeModal()} data-test="manage-access-close">
					<RiCloseCircleLine size={20} />
				</button>
			</div>
			<div className="divide-y divide-gray-200">
				<div data-test="user-info">
					<h2
						className="text-2xl font-bold mb-4"
						data-test="manage-access-title"
					>
						Give subscription to{' '}
						{user.firstName && user.lastName ? (
							<p>
								{user.firstName} {user.lastName}
							</p>
						) : (
							<p>{user.username}</p>
						)}
					</h2>
					<span className="py-4">
						This user is a{' '}
						<p className="font-bold inline-block">
							{showUserRole(user.roles[0])}
						</p>{' '}
						account
					</span>
				</div>
				<div className="py-4">
					<p className="font-bold">Account status</p>
					<span className="">{user.applicationRequest}</span>
				</div>
				<div className="py-4">
					<p className="font-bold">Subscription</p>
					{!user.subscription ? (
						<p data-test="no-subscription">No subscription</p>
					) : (
						<p className="flex">{subscriptionStatus(user, 'flex')}</p>
					)}
				</div>
				<div className="py-4">
					{isCancellableSubscription ? (
						<button
							disabled={isLoading}
							onClick={(e) => handleCancelSubscription(e, user.id as number)}
							className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded disabled:opacity-50"
						>
							Cancel subscription
						</button>
					) : (
						<div>
							<p className="font-bold">Gift subscription</p>
							{user.roles.includes(AppRole.Therapist) ? (
								<div className="flex items-center gap-x-8">
									<button
										disabled={isLoading}
										className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded disabled:opacity-50"
										data-testid="plan-a-button"
										onClick={(e) =>
											handleSubscription(
												e,
												user.id as number,
												SubscriberPlanEnum.THERAPIST_A,
											)
										}
									>
										Plan A
									</button>
									<button
										disabled={isLoading}
										className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded disabled:opacity-50"
										data-testid="plan-b-button"
										onClick={(e) =>
											handleSubscription(
												e,
												user.id as number,
												SubscriberPlanEnum.THERAPIST_B,
											)
										}
									>
										Plan B
									</button>
								</div>
							) : (
								<button
									disabled={isLoading}
									className="mt-4 bg-blue-500 hover:bg-blue-400 text-white py-1 px-2 rounded disabled:opacity-50 my-4"
									onClick={(e) =>
										handleSubscription(
											e,
											user.id as number,
											SubscriberPlanEnum.PREMIUM,
										)
									}
								>
									Premium plan
								</button>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ManageSubscriptionBlock;
